import React, { useState, useEffect } from 'react';
import { CCard, CCardBody, CCardHeader, CListGroup, CListGroupItem, CButton, CSpinner, CRow, CCol } from '@coreui/react';
import axios from 'axios';
import '../../../scss/main.scss';
import { toast } from 'react-toastify';
import LinkRobotModal from './LinkRobotModal'; // Adjust the import path as needed
import config from '../../../utils/EnvironmentSwitcher'; // Import config utility

const RobotStatus = ({ fhirToken, authUser }) => {
      const [robotStatus, setRobotStatus] = useState([]);
      const [isLoading, setIsLoading] = useState(true);
      const [error, setError] = useState(null);
      const [linking, setLinking] = useState(false);
      const [isSyncingToken, setSyncingToken] = useState(false);
      const [isModalVisible, setIsModalVisible] = useState(false);
      const [liveData, setLiveData] = useState(null);
      const [wsConnections, setWsConnections] = useState({});
      const [liveDataMap, setLiveDataMap] = useState({});
      const [newDataReceived, setNewDataReceived] = useState({});
      const [wsMessages, setWsMessages] = useState({});
      const characterToFrequency = {
            A: 440,  // A4
            B: 493.88, // B4
            C: 523.25, // C5
            D: 587.33, // D5
            E: 659.25, // E5
            F: 698.46, // F5
            G: 783.99, // G5
            H: 880,  // A5
            I: 987.77, // B5
            J: 1046.50, // C6
            K: 1174.66, // D6
            L: 1318.51, // E6
            M: 1396.91, // F6
            N: 1567.98, // G6
            O: 1760,  // A6
            P: 1975.53, // B6
            Q: 2093, // C7
            R: 2349.32, // D7
            S: 2637.02, // E7
            T: 2793.83, // F7
            U: 3135.96, // G7
            V: 3520,  // A7
            W: 3951.07, // B7
            X: 4186.01, // C8
            Y: 4698.63, // D8
            Z: 5274.04, // E8
            0: 261.63, // Middle C
            1: 293.66, // D
            2: 329.63, // E
            3: 349.23, // F
            4: 392.00, // G
            5: 440.00, // A
            6: 493.88, // B
            7: 523.25, // C5
            8: 587.33, // D5
            9: 659.25, // E5
      };

      const playTokenAsNotes = (token) => {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            let startTime = audioContext.currentTime;

            token.split('').forEach((char, index) => {
                  const frequency = characterToFrequency[char.toUpperCase()];
                  if (frequency) {
                        const oscillator = audioContext.createOscillator();
                        oscillator.type = 'sine';
                        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
                        oscillator.connect(audioContext.destination);

                        // Schedule the note
                        oscillator.start(startTime);
                        oscillator.stop(startTime + 0.4); // Each note plays for 400ms
                        startTime += 0.5; // 100ms gap between notes
                  }
            });
      };

      const handlePlayNotes = () => {
            if (!fhirToken) {
                  toast.error('No token available to play.');
                  return;
            }

            playTokenAsNotes(fhirToken);
            toast.success('Playing token as notes.');
      };

      const fetchRobotStatus = async () => {
            try {
                  if (!authUser?.uid) {
                        setError('Missing user ID.');
                        setIsLoading(false);
                        return;
                  }

                  // Get a fresh ID token
                  const idToken = await authUser.getIdToken(true);

                  console.log('Fetching robot status for user:', authUser.uid);
                  console.log('Using FHIR URL:', config.fhirUrl);

                  const response = await axios.get(
                        `${config.fhirUrl}/fhir/Device?identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}`,
                        {
                              headers: { Authorization: `Bearer ${idToken}` },
                        }
                  );

                  console.log('Robot status response:', response.data);

                  // Check if we have entries
                  if (!response.data.entry || response.data.entry.length === 0) {
                        console.log('No robots found for user:', authUser.uid);
                        setRobotStatus([]);
                        setIsLoading(false);
                        return;
                  }

                  const robots = response.data.entry.map((entry) => {
                        const resource = entry.resource;
                        console.log('Processing robot resource:', resource);

                        return {
                              id: resource.id,
                              apiKey: resource.identifier?.find((id) => id.system === 'https://fhir.mieros.cloud/fhir/keys')?.value || 'N/A',
                              status: resource.status || 'unknown',
                              model: resource.model || 'N/A',
                              description: resource.type?.coding?.[0]?.display || 'N/A',
                              lastUpdated: resource.meta?.lastUpdated || 'N/A',
                              ip: null, // Placeholder for IP address from linking process
                              linkedAt: null, // Placeholder for linked timestamp
                        };
                  });

                  console.log('Processed robots:', robots);
                  setRobotStatus(robots);
                  setIsLoading(false);
            } catch (err) {
                  console.error('Error fetching robot status:', err);
                  setError('Failed to fetch robot status.');
                  setIsLoading(false);
            }
      };

      const handleLinkRobot = async (robot) => {
            try {
                  setLinking(true);

                  // Get a fresh ID token
                  const idToken = await authUser.getIdToken(true);

                  if (!idToken) {
                        toast.error("Firebase token is missing.");
                        return;
                  }

                  // Use fhirToken as the linking token
                  // const speech = new SpeechSynthesisUtterance(`Your linking token is `);
                  // window.speechSynthesis.speak(speech);

                  // handlePlayNotes();
                  const apiKey = robot.apiKey;
                  // Update the Device resource directly
                  // const response = await axios.put(
                  //       `https://fhir.mieros.cloud/fhir/Device/${robot.id}`,
                  //       {
                  //             resourceType: "Device",
                  //             id: robot.id,
                  //             status: "linked",
                  //             identifier: [
                  //                   { system: "https://fhir.mieros.cloud/fhir/ids", value: robot.id },
                  //                   { system: "https://fhir.mieros.cloud/fhir/keys", value: fhirToken },
                  //             ],
                  //       },
                  //       { headers: { Authorization: `Bearer ${fhirToken}` } }
                  // );

                  // Use Web Bluetooth API to link robot
                  const device = await navigator.bluetooth.requestDevice({
                        // filters: [{ services: ['12345678-1234-5678-1234-567812345678'] }],
                        acceptAllDevices: true, // Allow all devices for testing
                        // optionalServices: ['12345678-1234-5678-1234-567812345678'], // Your service UUID
                  });

                  const server = await device.gatt.connect();
                  const tokenChar = await server.getPrimaryService('12345678-1234-5678-1234-567812345678');

                  await tokenChar.writeValue(new TextEncoder().encode(fhirToken));
                  toast.success('Robot linked successfully via Bluetooth!');

                  // Update the robot status in the backend
                  const response = await axios.put(
                        `${config.fhirUrl}/fhir/Device/${robot.id}`,
                        {
                              resourceType: "Device",
                              id: robot.id,
                              status: "linked",
                              identifier: [
                                    { system: "https://fhir.mieros.cloud/fhir/ids", value: authUser.uid },
                                    { system: "https://fhir.mieros.cloud/fhir/ids", value: robot.id },
                                    { system: "https://fhir.mieros.cloud/fhir/keys", value: idToken },
                              ],
                        },
                        { headers: { Authorization: `Bearer ${idToken}` } }
                  );


                  if (response.status === 200) {
                        toast.success("Robot linked successfully.");
                        fetchRobotStatus(); // Refresh robot status
                  } else {
                        toast.error("Failed to link robot.");
                  }
                  const { ip, status, linkedAt } = response.data;
                  toast.success(`Robot linked successfully with IP: ${ip}`);

                  // Update robot's status with the new details
                  setRobotStatus((prevStatus) =>
                        prevStatus.map((r) =>
                              r.id === robot.id
                                    ? {
                                          ...r,
                                          status,
                                          ip,
                                          linkedAt,
                                    }
                                    : r
                        )
                  );
            } catch (err) {
                  console.error('Error linking robot:', err);
                  toast.error('Failed to link robot.');
            } finally {
                  setLinking(false);
            }
      };

      const handleLinkRobotSerial = async (robotData) => {
            try {
                  setLinking(true);
                  console.log('Linking robot with data:', robotData);
                  // Implement the logic to link the robot using the provided data
                  // Example: Use robotData.ssid, robotData.password, etc.
                  toast.success('Robot linked successfully via Serial!');
            } catch (err) {
                  console.error('Error linking robot via Serial:', err);
                  toast.error('Failed to link robot via Serial.');
            } finally {
                  setLinking(false);
            }
      };

      const syncFirebaseToken = async (robot) => {
            try {
                  setSyncingToken(true);
                  toast.info('Connecting to robot via Bluetooth...');

                  // Get the Firebase token from localStorage or auth context
                  const firebaseToken = await authUser.getIdToken(true);
                  const refreshToken = authUser?.stsTokenManager?.refreshToken;

                  if (!firebaseToken) {
                        toast.error('Firebase token not available');
                        return;
                  }

                  // Create token object similar to the file format
                  const tokenData = {
                        token: firebaseToken,
                        refresh_token: refreshToken || '',
                        updated_at: new Date().toISOString()
                  };

                  // Convert to JSON string
                  const tokenJson = JSON.stringify(tokenData, null, 6);

                  // Request Bluetooth device
                  const device = await navigator.bluetooth.requestDevice({
                        acceptAllDevices: true,
                        optionalServices: ['12345678-1234-5678-1234-567812345678'] // Your service UUID
                  });

                  toast.info('Device selected, connecting...');

                  // Connect to GATT server
                  const server = await device.gatt.connect();

                  // Get primary service
                  const service = await server.getPrimaryService('12345678-1234-5678-1234-567812345678');

                  // Get characteristic for token
                  const tokenChar = await service.getCharacteristic('dcba4321-8765-4321-8765-fedcbafedcba');

                  // Write token data to characteristic
                  // Break into chunks if needed (BLE has size limitations)
                  const encoder = new TextEncoder();
                  const tokenBytes = encoder.encode(tokenJson);

                  // Check if we need to chunk the data (BLE typically has a 20-byte limit per write)
                  const chunkSize = 20;

                  if (tokenBytes.length <= chunkSize) {
                        // Small enough to send in one go
                        await tokenChar.writeValue(tokenBytes);
                  } else {
                        // Need to chunk the data
                        for (let i = 0; i < tokenBytes.length; i += chunkSize) {
                              const chunk = tokenBytes.slice(i, i + chunkSize);
                              await tokenChar.writeValue(chunk);
                              // Small delay between chunks
                              await new Promise(resolve => setTimeout(resolve, 50));
                        }
                  }

                  toast.success('Firebase token synced successfully!');

                  // Update robot status in UI
                  setRobotStatus(prevStatus =>
                        prevStatus.map(r =>
                              r.id === robot.id
                                    ? { ...r, tokenSyncedAt: new Date().toISOString() }
                                    : r
                        )
                  );

            } catch (error) {
                  console.error('Error syncing Firebase token:', error);
                  toast.error(`Failed to sync token: ${error.message}`);
            } finally {
                  setSyncingToken(false);
            }
      };

      const connectWebSocket = (robot) => {
            // Close existing connection if any
            if (wsConnections[robot.id]) {
                  wsConnections[robot.id].close();
            }

            // Use the correct WebSocket URL
            const robotId = robot.id;

            // Get a fresh ID token for authentication
            authUser.getIdToken(true).then(idToken => {
                  // Include client=true parameter to identify as a web client
                  const wsUrl = `wss://robots.mieros.cloud/api/v1/robots/${robotId}/ws?client=true&token=${idToken}`;
                  
                  console.log(`Attempting to connect to WebSocket for robot ${robotId} using URL: ${wsUrl.substring(0, 60)}...`);

                  try {
                        const ws = new WebSocket(wsUrl);
                        
                        // Add a timeout to detect connection issues
                        const connectionTimeout = setTimeout(() => {
                              if (ws.readyState !== WebSocket.OPEN) {
                                    console.error(`WebSocket connection timeout for robot ${robotId}`);
                                    ws.close();
                                    toast.error("WebSocket connection timeout");
                              }
                        }, 10000); // 10 seconds timeout
                        
                        // Set up a ping interval reference
                        let pingInterval = null;
                        
                        ws.onopen = () => {
                              // Clear the connection timeout
                              clearTimeout(connectionTimeout);
                              
                              console.log(`WebSocket connected for robot ${robot.id} at ${new Date().toISOString()}`);
                              toast.success(`Live updates enabled for ${robot.description || robot.id}`);
                              
                              // Send a registration message
                              ws.send(JSON.stringify({
                                    type: 'register_web_client',
                                    robotId: robot.id,
                                    clientId: authUser.uid,
                                    timestamp: Date.now()
                              }));

                              // Store the connection
                              setWsConnections(prev => ({
                                    ...prev,
                                    [robot.id]: ws
                              }));
                              
                              // Set up ping interval only after successful connection
                              pingInterval = setInterval(() => {
                                    if (ws.readyState === WebSocket.OPEN) {
                                          ws.send(JSON.stringify({ 
                                                type: 'ping',
                                                timestamp: Date.now() 
                                          }));
                                          console.log(`Ping sent for robot ${robot.id} from website`);
                                    } else {
                                          // If connection is no longer open, clear the interval
                                          clearInterval(pingInterval);
                                    }
                              }, 30000);
                        };

                        ws.onmessage = (event) => {
                              // Log the raw message
                              console.log(`⚡ WebSocket message received for robot ${robot.id}:`, event.data);
                              
                              // Store the message in state for display
                              setWsMessages(prev => {
                                    const robotMessages = prev[robot.id] || [];
                                    // Keep only the last 10 messages to avoid excessive memory usage
                                    const updatedMessages = [...robotMessages, {
                                          time: new Date().toLocaleTimeString(),
                                          data: event.data
                                    }].slice(-10);
                                    
                                    return {
                                          ...prev,
                                          [robot.id]: updatedMessages
                                    };
                              });
                              
                              try {
                                    const data = JSON.parse(event.data);
                                    console.log(`📊 Parsed data for robot ${robot.id}:`, data);

                                    // Check if this is an authentication success message
                                    if (data.type === 'auth_success') {
                                          console.log("🔐 Authentication successful for robot", data.robotId);
                                          toast.success("WebSocket connection established");
                                          return;
                                    }

                                    // Log all message types
                                    console.log(`📝 Message type for robot ${robot.id}:`, data.type || 'status update');

                                    // Check specifically for status updates
                                    if (data.status) {
                                          console.log(`🤖 Status update received for robot ${robot.id}:`, data.status);

                                          // Update live data for this robot
                                          setLiveDataMap(prev => {
                                                const newMap = {
                                                      ...prev,
                                                      [robot.id]: data
                                                };
                                                console.log(`✅ Updated liveDataMap for robot ${robot.id}:`, newMap[robot.id]);
                                                console.log(`✅ Full liveDataMap:`, newMap);
                                                return newMap;
                                          });

                                          // Add visual indication of new data
                                          setNewDataReceived(prev => ({
                                                ...prev,
                                                [robot.id]: true
                                          }));

                                          // Clear the flag after a short delay
                                          setTimeout(() => {
                                                setNewDataReceived(prev => ({
                                                      ...prev,
                                                      [robot.id]: false
                                                }));
                                          }, 2000);
                                    }
                              } catch (error) {
                                    console.error('❌ Error parsing WebSocket message:', error);
                                    console.log('📄 Raw message:', event.data);
                              }
                        };

                        // Add more detailed error handling
                        ws.onerror = (error) => {
                              console.error(`❌ WebSocket error for robot ${robot.id} at ${new Date().toISOString()}:`, error);
                              console.error(`WebSocket URL: ${wsUrl.substring(0, 60)}...`); // Log partial URL for debugging
                              console.error(`WebSocket readyState: ${ws.readyState}`);
                              toast.error(`WebSocket error: ${error.message || 'Connection failed'}`);
                        };

                        // In the onclose handler, make sure to clear the ping interval
                        ws.onclose = (event) => {
                              console.log(`🔌 WebSocket closed for robot ${robot.id} at ${new Date().toISOString()}`);
                              console.log(`Close code: ${event.code}`);
                              console.log(`Close reason: ${event.reason || 'No reason provided'}`);
                              console.log(`Clean close: ${event.wasClean}`);
                              console.log(`WebSocket URL was: ${wsUrl.substring(0, 60)}...`);

                              // Clear the connection timeout
                              if (connectionTimeout) {
                                    clearTimeout(connectionTimeout);
                              }
                              
                              // Clear the ping interval
                              if (pingInterval) {
                                    clearInterval(pingInterval);
                              }

                              // Remove the connection
                              setWsConnections(prev => {
                                    const newConnections = { ...prev };
                                    delete newConnections[robot.id];
                                    return newConnections;
                              });

                              // Show appropriate message based on close code
                              if (event.code === 4001) {
                                    toast.error(`Authentication failed: No token provided`);
                              } else if (event.code === 4003) {
                                    toast.error(`Authentication failed: Invalid token`);
                              } else if (event.code !== 1000) {
                                    toast.warning(`Connection lost for ${robot.description || robot.id}`);
                                    
                                    // Don't automatically reconnect
                                    // We've removed the automatic reconnection code here
                              }
                        };

                        return ws;
                  } catch (error) {
                        console.error(`❌ Error creating WebSocket for robot ${robot.id}:`, error);
                        toast.error(`Failed to connect: ${error.message}`);
                        return null;
                  }
            }).catch(error => {
                  console.error('❌ Error getting ID token for WebSocket:', error);
                  toast.error('Authentication failed for WebSocket connection');
            });
      };

      const disconnectWebSocket = (robotId) => {
            if (wsConnections[robotId]) {
                  wsConnections[robotId].close();
            }
      };

      const debugLiveData = (robotId) => {
            console.log("Current liveDataMap for robot", robotId, ":", liveDataMap[robotId]);
            if (liveDataMap[robotId]) {
                  console.log("Status:", liveDataMap[robotId].status);
                  console.log("Status.status:", liveDataMap[robotId].status?.status);
                  console.log("Battery:", liveDataMap[robotId].status?.battery);
            } else {
                  console.log("No live data available for robot", robotId);
            }
            toast.info("Check console for live data debug info");
      };

      const checkWebSocketStatus = (robotId) => {
            console.log(`Checking WebSocket status for robot ${robotId}`);
            if (wsConnections[robotId]) {
                  console.log(`WebSocket is open for robot ${robotId}`);
                  toast.success("WebSocket connection is open");
            } else {
                  console.log(`WebSocket is closed for robot ${robotId}`);
                  toast.warning("WebSocket connection is closed");
            }
      };

      // Add this function to send commands to the robot
      const sendRobotCommand = (robotId, command, speed = 0.8) => {
            const ws = wsConnections[robotId];
            
            if (!ws || ws.readyState !== WebSocket.OPEN) {
                  toast.error("WebSocket connection not open. Cannot send command.");
                  return false;
            }
            
            try {
                  ws.send(JSON.stringify({
                        command,
                        speed,
                        timestamp: Date.now()
                  }));
                  
                  console.log(`Command sent to robot ${robotId}: ${command} (speed: ${speed})`);
                  toast.info(`Command sent: ${command}`);
                  return true;
            } catch (error) {
                  console.error(`Error sending command to robot ${robotId}:`, error);
                  toast.error(`Failed to send command: ${error.message}`);
                  return false;
            }
      };

      // Add this function to help with debugging
      const testWebSocketDirectly = (robotId) => {
            authUser.getIdToken(true).then(idToken => {
                  // Log the test for reference
                  console.log(`Testing direct WebSocket connection for robot ${robotId}`);
                  
                  // Create a test function that can be run in the console
                  window.testWsConnection = (urlFormat) => {
                        let testUrl;
                        
                        if (urlFormat === 1) {
                              testUrl = `wss://robots.mieros.cloud/ws/client/${robotId}?client=true&token=${idToken}`;
                              console.log("Testing URL format 1: Client path");
                        } else if (urlFormat === 2) {
                              testUrl = `wss://robots.mieros.cloud/api/v1/robots/${robotId}/ws?client=true&token=${idToken}`;
                              console.log("Testing URL format 2: API path");
                        } else if (urlFormat === 3) {
                              testUrl = `wss://robots.mieros.cloud/ws/client/${robotId}?client=true&token=${encodeURIComponent(idToken)}`;
                              console.log("Testing URL format 3: Encoded token");
                        }
                        
                        console.log(`Test URL: ${testUrl.substring(0, 60)}...`);
                        
                        const testWs = new WebSocket(testUrl);
                        
                        testWs.onopen = () => {
                              console.log("✅ Test connection OPENED successfully!");
                              
                              // Send a test message
                              testWs.send(JSON.stringify({
                                    type: 'test_message',
                                    message: 'Hello from direct test',
                                    timestamp: Date.now()
                              }));
                        };
                        
                        testWs.onmessage = (event) => {
                              console.log("📩 Test received message:", event.data);
                        };
                        
                        testWs.onerror = (error) => {
                              console.error("❌ Test connection ERROR:", error);
                        };
                        
                        testWs.onclose = (event) => {
                              console.log(`🔌 Test connection CLOSED. Code: ${event.code}, Reason: ${event.reason || 'No reason'}, Clean: ${event.wasClean}`);
                        };
                        
                        return "Test connection initiated. Check console for results.";
                  };
                  
                  // Log instructions
                  console.log("To test different URL formats, run in console:");
                  console.log("window.testWsConnection(1) - for client path");
                  console.log("window.testWsConnection(2) - for API path");
                  console.log("window.testWsConnection(3) - for encoded token");
                  
                  toast.info("WebSocket test function added to window. Check console for instructions.");
            });
      };

      useEffect(() => {
            fetchRobotStatus();
      }, [fhirToken, authUser]);

      useEffect(() => {
            return () => {
                  // Clean up WebSocket connections when component unmounts
                  Object.values(wsConnections).forEach(ws => {
                        if (ws && ws.readyState === WebSocket.OPEN) {
                              ws.close();
                        }
                  });
            };
      }, [wsConnections]);

      if (isLoading) {
            return (
                  <div className="text-center">
                        <CSpinner color="primary" />
                        <p>Loading Robot Status...</p>
                  </div>
            );
      }

      if (error) {
            return <p className="text-danger text-center">{error}</p>;
      }

      return (
            <CCard className="shadow robot-status-card">
                  <CCardHeader className="text-center text-primary">
                        <h4>Robot Status</h4>
                  </CCardHeader>
                  <CCardBody>
                        {isLoading ? (
                              <div className="text-center">
                                    <CSpinner color="primary" />
                                    <p>Loading Robot Status...</p>
                              </div>
                        ) : error ? (
                              <p className="text-danger text-center">{error}</p>
                        ) : (
                              <>
                                    {robotStatus.length > 0 ? (
                                          <CListGroup>
                                                {robotStatus.map((robot, index) => (
                                                      <CListGroupItem key={index} className="robot-list-item">
                                                            <div className="robot-info-mobile">
                                                                  <div className="info-row">
                                                                        <span className="info-label">ID:</span>
                                                                        <span className="info-value">{robot.id}</span>
                                                                  </div>
                                                                  <div className="info-row">
                                                                        <span className="info-label">API Key:</span>
                                                                        <span className="info-value">{robot.apiKey}</span>
                                                                  </div>
                                                                  <div className="info-row">
                                                                        <span className="info-label">Status:</span>
                                                                        <span className="info-value">{robot.status}</span>
                                                                  </div>
                                                                  <div className="info-row">
                                                                        <span className="info-label">Model:</span>
                                                                        <span className="info-value">{robot.model}</span>
                                                                  </div>
                                                                  <div className="info-row">
                                                                        <span className="info-label">Last Updated:</span>
                                                                        <span className="info-value">{robot.lastUpdated}</span>
                                                                  </div>
                                                                  <div className="info-row">
                                                                        <span className="info-label">Description:</span>
                                                                        <span className="info-value">{robot.description}</span>
                                                                  </div>
                                                                  {robot.ip && (
                                                                        <>
                                                                              <div className="info-row">
                                                                                    <span className="info-label">IP:</span>
                                                                                    <span className="info-value">{robot.ip}</span>
                                                                              </div>
                                                                              <div className="info-row">
                                                                                    <span className="info-label">Linked At:</span>
                                                                                    <span className="info-value">{robot.linkedAt}</span>
                                                                              </div>
                                                                        </>
                                                                  )}
                                                                  {robot.tokenSyncedAt && (
                                                                        <div className="info-row">
                                                                              <span className="info-label">Token Synced:</span>
                                                                              <span className="info-value">{robot.tokenSyncedAt}</span>
                                                                        </div>
                                                                  )}
                                                            </div>

                                                            {liveDataMap[robot.id] && (
                                                                  <div className={`live-status ${newDataReceived[robot.id] ? 'new-data-received' : ''}`}>
                                                                        <div className="status-indicator">
                                                                              <span
                                                                                    className={`status-dot ${liveDataMap[robot.id]?.status?.status === 'online'
                                                                                                        ? 'bg-success'
                                                                                                        : 'bg-danger'
                                                                                                  }`}
                                                                              ></span>
                                                                              <span>
                                                                                    {liveDataMap[robot.id]?.status?.status || 'unknown'}
                                                                              </span>
                                                                              <span className="live-indicator">Live</span>
                                                                        </div>

                                                                        {liveDataMap[robot.id]?.status?.battery !== undefined && (
                                                                              <div className="battery-status">
                                                                                    <strong>Battery:</strong> {liveDataMap[robot.id].status.battery}%
                                                                                    <div className="battery-bar">
                                                                                          <div
                                                                                                className="battery-level"
                                                                                                style={{
                                                                                                      width: `${liveDataMap[robot.id].status.battery}%`,
                                                                                                      backgroundColor: liveDataMap[robot.id].status.battery > 20
                                                                                                            ? '#28a745'
                                                                                                            : '#dc3545'
                                                                                                }}
                                                                                          ></div>
                                                                                    </div>
                                                                              </div>
                                                                        )}

                                                                        {liveDataMap[robot.id]?.status?.position && (
                                                                              <div className="position-status">
                                                                                    <strong>Position:</strong> X: {liveDataMap[robot.id].status.position.x.toFixed(2)},
                                                                                    Y: {liveDataMap[robot.id].status.position.y.toFixed(2)}
                                                                              </div>
                                                                        )}

                                                                        {liveDataMap[robot.id]?.timestamp && (
                                                                              <div className="timestamp">
                                                                                    <small>
                                                                                          Last update: {new Date(liveDataMap[robot.id].timestamp * 1000).toLocaleTimeString()}
                                                                                    </small>
                                                                              </div>
                                                                        )}
                                                                  </div>
                                                            )}

                                                            <div className="robot-action-buttons">
                                                                  {!robot.ip && (
                                                                        <CButton
                                                                              color="primary"
                                                                              onClick={() => setIsModalVisible(true)}
                                                                              disabled={linking || isSyncingToken}
                                                                              className="me-2"
                                                                        >
                                                                              {linking ? 'Linking...' : 'Link Robot'}
                                                                        </CButton>
                                                                  )}
                                                                  <CButton
                                                                        color="info"
                                                                        onClick={() => syncFirebaseToken(robot)}
                                                                        disabled={isSyncingToken}
                                                                        className="me-2"
                                                                  >
                                                                        {isSyncingToken ? (
                                                                              <>
                                                                                    <CSpinner size="sm" className="me-1" /> Syncing...
                                                                  </>
                                                            ) : (
                                                                              'Sync Firebase Token'
                                                                        )}
                                                                  </CButton>
                                                                  <CButton
                                                                        color={wsConnections[robot.id] ? "success" : "secondary"}
                                                                        onClick={() => wsConnections[robot.id]
                                                                              ? disconnectWebSocket(robot.id)
                                                                              : connectWebSocket(robot)
                                                                        }
                                                                        className="me-2"
                                                                  >
                                                                        {wsConnections[robot.id] ? 'Disconnect Live' : 'Connect Live Manually'}
                                                                  </CButton>
                                                                  <CButton
                                                                        color="warning"
                                                                        onClick={() => checkWebSocketStatus(robot.id)}
                                                                        className="me-2"
                                                                  >
                                                                        Check WS Status
                                                                  </CButton>
                                                                  <CButton
                                                                        color="info"
                                                                        onClick={() => debugLiveData(robot.id)}
                                                                        className="ms-2"
                                                                  >
                                                                        Debug Live Data
                                                                  </CButton>
                                                                  <CButton
                                                                        color="danger"
                                                                        onClick={() => {
                                                                              const ws = wsConnections[robot.id];
                                                                              if (ws && ws.readyState === WebSocket.OPEN) {
                                                                                    ws.send(JSON.stringify({
                                                                                          type: 'debug_ping',
                                                                                          robotId: robot.id,
                                                                                          clientId: authUser.uid,
                                                                                          message: 'Testing web client connection',
                                                                                          timestamp: Date.now()
                                                                                    }));
                                                                                    console.log(`Debug ping sent for robot ${robot.id}`);
                                                                                    toast.info("Debug ping sent");
                                                                              } else {
                                                                                    console.log(`Cannot send debug ping - WebSocket not open for robot ${robot.id}`);
                                                                                    toast.warning("WebSocket not connected");
                                                                              }
                                                                        }}
                                                                        className="me-2"
                                                                  >
                                                                        Debug Connection
                                                                  </CButton>
                                                                  <CButton
                                                                        color="secondary"
                                                                        onClick={() => testWebSocketDirectly(robot.id)}
                                                                        className="me-2"
                                                                  >
                                                                        Test WS Directly
                                                                  </CButton>
                                                            </div>

                                                            {/* WebSocket Messages Display */}
                                                            {wsConnections[robot.id] && (
                                                                  <div className="ws-messages-container">
                                                                        <h5>WebSocket Messages</h5>
                                                                        {wsMessages[robot.id] && wsMessages[robot.id].length > 0 ? (
                                                                              <div className="ws-messages-list">
                                                                                    {wsMessages[robot.id].map((msg, idx) => (
                                                                                          <div key={idx} className="ws-message">
                                                                                                <span className="ws-message-time">{msg.time}</span>
                                                                                                <pre className="ws-message-data">{msg.data}</pre>
                                                                                          </div>
                                                                                    ))}
                                                                              </div>
                                                                        ) : (
                                                                              <p className="mb-2">No messages received yet...</p>
                                                                        )}
                                                                  </div>
                                                            )}

                                                            {wsConnections[robot.id] && (
                                                                  <div className="robot-control-panel">
                                                                        <h5>Robot Controls</h5>
                                                                        <div className="control-buttons">
                                                                              <CButton 
                                                                                    color="primary" 
                                                                                    onClick={() => sendRobotCommand(robot.id, 'forward')}
                                                                                    className="control-btn"
                                                                              >
                                                                                    Forward
                                                                              </CButton>
                                                                              <CButton 
                                                                                    color="primary" 
                                                                                    onClick={() => sendRobotCommand(robot.id, 'backward')}
                                                                                    className="control-btn"
                                                                              >
                                                                                    Backward
                                                                              </CButton>
                                                                              <CButton 
                                                                                    color="primary" 
                                                                                    onClick={() => sendRobotCommand(robot.id, 'left')}
                                                                                    className="control-btn"
                                                                              >
                                                                                    Left
                                                                              </CButton>
                                                                              <CButton 
                                                                                    color="primary" 
                                                                                    onClick={() => sendRobotCommand(robot.id, 'right')}
                                                                                    className="control-btn"
                                                                              >
                                                                                    Right
                                                                              </CButton>
                                                                              <CButton 
                                                                                    color="danger" 
                                                                                    onClick={() => sendRobotCommand(robot.id, 'stop')}
                                                                                    className="control-btn"
                                                                              >
                                                                                    Stop
                                                                              </CButton>
                                                                        </div>
                                                                  </div>
                                                            )}
                                                      </CListGroupItem>
                                                ))}
                                          </CListGroup>
                                    ) : (
                                          <p className="mb-2 text-center">No robots linked yet.</p>
                                    )}
                                    <LinkRobotModal
                                          isVisible={isModalVisible}
                                          onClose={() => setIsModalVisible(false)}
                                          handleLinkRobotSerial={handleLinkRobotSerial}
                                          authUser={authUser}
                                    />
                              </>
                        )}
                  </CCardBody>
            </CCard>
      );
};

export default RobotStatus;


